import { Offer } from "@/services/backend/vbs/offers/types/offer";
import t from "@/lang/lang";
import { useState } from "react";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { useParams } from "react-router";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  UpdateDateRequest,
  useVbsOfferShowQuery,
  useVbsOfferUpdateDateMutation,
} from "@/services/backend/vbs/offers/api/offer-api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { DateInput } from "@/shared/components/form/date-input";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { PricingCards } from "@/services/backend/vbs/pricing/components/pricing-cards";
import { Textblocks } from "@/services/backend/textblocks/components/textblocks";
import { GSMSubdomain } from "@/shared/types/gsm-subdomain";
import { PricingByAreaCard } from "@/services/backend/vbs/pricing/components/pricing-by-area-card";

export function OfferDataRoute() {
  const { offerId } = useParams();

  const {
    data: offer,
    isLoading,
    error,
  } = useVbsOfferShowQuery({
    id: offerId!,
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  return <OfferView offer={offer!} />;
}

export function OfferView({ offer }: { offer: Offer }) {
  const disabled = offer.immutable;

  return (
    <div className="grid gap-2">
      <BaseData offer={offer} />
      <PricingCards pricingId={offer.pricingId} disabled={disabled} />
      <PricingByAreaCard
        pricingByAreaId={offer.pricingByAreaId}
        disabled={disabled}
      />
      <Textblocks
        subdomain={GSMSubdomain.VBS}
        documentKind={offer!.documentKind}
        documentId={offer!.id}
        title={t("Bemerkungen im Angebot")}
        defaultOpen={false}
      />
    </div>
  );
}

function BaseData({ offer }: { offer: Offer }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Angebot")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid lg:grid-cols-2">
          <DateView offer={offer} />
        </div>
      </CardContent>
    </Card>
  );
}

function DateView(props: { offer: Offer }) {
  const { offer } = props;
  const [request, setRequest] = useState<UpdateDateRequest>({
    id: offer.id,
    date: offer.offerDate,
  });
  const mutation = useVbsOfferUpdateDateMutation();
  const { isLoading, error } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: offer.id,
      date: offer.offerDate,
    },
    { delay: 0 },
  );

  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();

  return (
    <DateInput
      label={t("Angebotsdatum")}
      date={request.date}
      onDateChange={(date) => {
        resetNotReadyErrorField("date");
        setRequest({ ...request, date: date ?? "" });
      }}
      errors={inputValidationErrorMessages(error || notReadyError, "date")}
      disabled={isLoading || offer.immutable}
    />
  );
}
